import { createRouter, createWebHistory } from 'vue-router'
import { getAuthInstance, ensureAuthInitialized } from '../firebase/app'
import { useAuthStore } from '../stores/auth'
import { handleChunkLoadError } from '../utils/chunkLoadHandler'
// Lazy-load heavy initial views to reduce main bundle size
const MySubscriptions = () => import('../views/MySubscriptions.vue')
const MyEvents = () => import('../views/admin/MyEvents.vue')
const EventSubscribe = () => import('../views/EventSubscribe.vue')
const Subscription = () => import('../views/Subscription.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: { name: 'subscriptions' }
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: () => import('../views/FirebaseLogin.vue'),
      props: (route) => ({ inviteToken: route.params.token })
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/FirebaseLogin.vue')
    },
    {
      path: '/subscriptions',
      name: 'subscriptions',
      component: MySubscriptions,
      meta: { requiresAuth: true }
    },
    {
      path: '/subscriptions/:subscriptionUid',
      name: 'subscription',
      props: true,
      component: Subscription,
      meta: { requiresAuth: true }
    },
    {
      path: '/subscriptions/subscribe/:eventUid',
      name: 'eventSubscribe',
      props: true,
      component: EventSubscribe,
      meta: { requiresAuth: true }
    },
    {
      path: '/events',
      name: 'eventList',
      component: MyEvents,
      meta: { requiresAuth: true }
    },
    {
      path: '/event/create',
      name: 'eventCreate',
      component: () => import('../views/admin/EventCreate.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/event/:eventUid',
      name: 'eventView',
      props: true,
      component: () => import('../views/admin/EventView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          name: 'eventSubscribers',
          path: 'subscribers',
          props: true,
          component: () => import('../views/admin/EventSubscribers.vue'),
          meta: { requiresAuth: true }
        },
        {
          name: 'eventConfig',
          path: 'config',
          props: true,
          component: () => import('../views/admin/EventConfig.vue'),
          meta: { requiresAuth: true }
          // TODO: children for tabs !!
        },
        {
          name: 'activityCreate',
          path: 'activity/create',
          props: true,
          component: () => import('../views/admin/ActivityCreate.vue'),
          meta: { requiresAuth: true }
        },
        {
          name: 'activityEdit',
          path: 'activity/:activityUid',
          props: true,
          component: () => import('../views/admin/ActivityEdit.vue'),
          meta: { requiresAuth: true }
        },
        {
          name: 'subscriptionEdit',
          path: 'subscription/:subscriptionUid/edit',
          props: true,
          component: () => import('../views/admin/SubscriptionEdit.vue'),
          meta: { requiresAuth: true }
        }
      ]
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/MyProfile.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/root-admin',
      name: 'rootAdmin',
      component: () => import('../views/RootAdmin.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/debug-errors',
      name: 'debugErrors',
      component: () => import('../views/DebugErrorReporting.vue')
    },
    {
      path: '/teams/onboarding',
      name: 'leaderOnboarding',
      component: () => import('../views/teams/LeaderOnboarding.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/teams/my-teams',
      name: 'myTeams',
      component: () => import('../views/teams/MyTeams.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      component: () => import('../views/NotFound.vue')
    }
  ]
})

// Global auth guard + invite deep-link handling
router.beforeEach(async (to, from, next) => {
  // Force invite deep-link resolution if initial route appears as '/'
  try {
    const path = window.location?.pathname || ''
    if ((!to.name || to.path === '/') && path.startsWith('/invite/')) {
      const token = path.replace('/invite/', '').split('/')[0]
      return next({ name: 'invite', params: { token } })
    }
  } catch {
    /* ignore */
  }

  if (to.name === 'login') {
    await ensureAuthInitialized()
    const isAuthed = !!getAuthInstance().currentUser

    if (isAuthed) {
      const redirectTarget =
        typeof to.query.redirect === 'string' && to.query.redirect
          ? to.query.redirect
          : '/subscriptions'
      return next(redirectTarget)
    }

    return next()
  }

  if (to.name === 'invite') {
    await ensureAuthInitialized()
    const currentUser = getAuthInstance().currentUser

    if (!currentUser) return next()

    try {
      const authStore = useAuthStore()
      await authStore.login(currentUser, to.params.token)
      const redirectTarget =
        typeof to.query.redirect === 'string' && to.query.redirect
          ? to.query.redirect
          : '/subscriptions'
      return next(redirectTarget)
    } catch {
      return next()
    }
  }

  const isProtected = to.matched.some((r) => r.meta && r.meta.requiresAuth)
  if (!isProtected) return next()

  // Wait until Firebase restores any persisted session
  await ensureAuthInitialized()
  const isAuthed = !!getAuthInstance().currentUser
  if (!isAuthed) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }
  return next()
})

// Handle chunk load errors during navigation
router.onError((error) => {
  handleChunkLoadError(error)
})

export default router
